/**
 * Shared credit-card form pieces used by both the SPA/light-DOM payment path
 * (`PaymentPage.tsx`) and the Dorsia-origin payment iframe (`EmbeddedPaymentFrame.tsx`).
 *
 * `CardFields` is a purely presentational Stripe split-Elements form: it renders
 * the card number/expiry/cvc + billing name/postal/country fields, tracks
 * completion/validity, and hands a ready-to-tokenize
 * `{ stripe, elements, cardNumberElement, billingDetails }` payload to whatever
 * `onSubmit` strategy the caller provides. It has **no** knowledge of widget
 * state, auth, or the backend — so it is safe to mount inside the isolated iframe
 * (which has none of those) as well as inside the SPA flow.
 *
 * It must be rendered inside a Stripe `<Elements>` provider (it calls
 * `useStripe`/`useElements`).
 */
import { useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  Stripe,
  StripeElements,
  StripeCardNumberElement,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import { getCountries } from 'react-phone-number-input';
import COUNTRY_LABELS from 'react-phone-number-input/locale/en.json';

import styles from '../EventPage/EventPage.module.css';

// ── Stripe appearance ────────────────────────────────────────────────────────

export const STRIPE_ELEMENT_STYLE = {
  base: {
    fontFamily: 'Sailec, sans-serif',
    fontSize: '15px',
    color: '#0D0A06',
    '::placeholder': { color: '#8d8d8d' },
  },
  invalid: { color: '#e63535' },
} as const;

export const STRIPE_OPTIONS: StripeElementsOptions = {
  mode: 'setup',
  currency: 'usd',
  appearance: {
    theme: 'stripe',
    variables: {
      fontFamily: 'Sailec',
      borderRadius: '10px',
      colorBackground: '#f4f4f4',
    },
  },
  fonts: [{ family: 'Sailec', src: 'url(https://book.dorsia.com/fonts/stripe-font.otf)' }],
};

// ── Billing / postal-code validation ─────────────────────────────────────────

export const BILLING_COUNTRIES = getCountries()
  .map((code) => [code, COUNTRY_LABELS[code as keyof typeof COUNTRY_LABELS] ?? code] as const)
  .sort((a, b) => a[1].localeCompare(b[1]));

const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
  US: /^\d{5}(?:-\d{4})?$/,
  CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
  GB: /^(GIR ?0AA|(?:(?:[A-Z][0-9]{1,2}|[A-Z][A-HJ-Y][0-9]{1,2}|[A-Z][0-9][A-Z]|[A-Z][A-HJ-Y][0-9][A-Z]) ?[0-9][A-Z]{2}))$/i,
  AU: /^\d{4}$/,
  BR: /^\d{5}-?\d{3}$/,
  DE: /^\d{5}$/,
  ES: /^\d{5}$/,
  FR: /^\d{5}$/,
  IN: /^\d{6}$/,
  IT: /^\d{5}$/,
  JP: /^\d{3}-?\d{4}$/,
  MX: /^\d{5}$/,
  NL: /^\d{4}\s?[A-Z]{2}$/i,
};

const OPTIONAL_POSTAL_CODE_COUNTRIES = new Set([
  'AE', 'AG', 'AW', 'BS', 'BZ', 'DM', 'FJ', 'GD', 'GH', 'GM', 'GY', 'HK', 'JM',
  'KE', 'KI', 'KN', 'LC', 'MO', 'MU', 'NR', 'PA', 'QA', 'RW', 'SB', 'SC', 'SL',
  'SO', 'SR', 'ST', 'SY', 'TG', 'TK', 'TL', 'TO', 'TT', 'TV', 'UG', 'VU', 'WS',
  'YE', 'ZW',
]);

export const isPostalCodeRequired = (countryCode: string): boolean =>
  !OPTIONAL_POSTAL_CODE_COUNTRIES.has(countryCode);

export const isPostalCodeValid = (postalCode: string, countryCode: string): boolean => {
  const normalizedPostalCode = postalCode.trim().toUpperCase();

  if (!normalizedPostalCode) {
    return !isPostalCodeRequired(countryCode);
  }

  const pattern = POSTAL_CODE_PATTERNS[countryCode];
  if (pattern) {
    return pattern.test(normalizedPostalCode);
  }

  return /^[A-Z0-9][A-Z0-9 -]{0,11}$/i.test(normalizedPostalCode);
};

// ── Shared card form ─────────────────────────────────────────────────────────

export interface CardBillingDetails {
  name: string;
  email?: string;
  address: { postal_code?: string; country: string };
}

export interface CardSubmitArgs {
  stripe: Stripe;
  elements: StripeElements;
  cardNumberElement: StripeCardNumberElement;
  billingDetails: CardBillingDetails;
}

interface CardFieldsProps {
  /** Strategy that tokenizes the collected card. Return `{ error }` to surface a
   *  message inline; return nothing / no error to signal success. */
  onSubmit: (args: CardSubmitArgs) => Promise<{ error?: string } | void>;
  submitLabel?: string;
  submittingLabel?: string;
  defaultFirstName?: string;
  defaultLastName?: string;
  /** Billing email attached to the payment method (the iframe has no user context). */
  email?: string;
  /** Reports the detected card brand as the number is typed (e.g. 'visa'). */
  onCardBrandChange?: (brand: string) => void;
}

/**
 * Presentational Stripe split-Elements card form. Renders the card + billing
 * fields, manages completion/validity, and delegates tokenization to `onSubmit`.
 * Must be rendered inside a Stripe `<Elements>` provider.
 */
export function CardFields({
  onSubmit,
  submitLabel = 'Add Credit Card',
  submittingLabel = 'Adding...',
  defaultFirstName = '',
  defaultLastName = '',
  email,
  onCardBrandChange,
}: CardFieldsProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);

  const postalCodeRequired = isPostalCodeRequired(country);
  const postalCodeValid = isPostalCodeValid(postalCode, country);

  const formValid =
    cardComplete &&
    expiryComplete &&
    cvcComplete &&
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    postalCodeValid;

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage(undefined);

    if (!postalCodeValid) {
      setErrorMessage(
        postalCodeRequired
          ? 'Please enter a valid postal code for the selected country.'
          : 'Please enter a valid postal code, or leave it blank.'
      );
      setSubmitting(false);
      return;
    }

    if (!stripe || !elements) {
      setSubmitting(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setErrorMessage('Card form not ready');
      setSubmitting(false);
      return;
    }

    const billingDetails: CardBillingDetails = {
      name: `${firstName} ${lastName}`,
      email,
      address: { postal_code: postalCode.trim() || undefined, country },
    };

    try {
      const result = await onSubmit({ stripe, elements, cardNumberElement, billingDetails });
      if (result?.error) {
        setErrorMessage(result.error);
      }
    } catch {
      setErrorMessage('Sorry, there was an issue. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.formSection}>
      {errorMessage && <div className={styles.error}>{errorMessage}</div>}

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Card number</label>
        <div className={styles.fieldInput} style={{ padding: '10px 20px' }}>
          <CardNumberElement
            options={{ style: STRIPE_ELEMENT_STYLE }}
            onChange={(e) => {
              setCardComplete(e.complete);
              if (e.brand && e.brand !== 'unknown') onCardBrandChange?.(e.brand);
            }}
          />
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Expiry</label>
          <div className={styles.fieldInput} style={{ padding: '10px 20px' }}>
            <CardExpiryElement
              options={{ style: STRIPE_ELEMENT_STYLE }}
              onChange={(e) => setExpiryComplete(e.complete)}
            />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Security code</label>
          <div className={styles.fieldInput} style={{ padding: '10px 20px' }}>
            <CardCvcElement
              options={{ style: STRIPE_ELEMENT_STYLE }}
              onChange={(e) => setCvcComplete(e.complete)}
            />
          </div>
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>First name</label>
          <input
            type="text"
            aria-label="First name"
            className={styles.fieldInput}
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Last name</label>
          <input
            type="text"
            aria-label="Last name"
            className={styles.fieldInput}
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
          />
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {postalCodeRequired ? 'Billing postal code' : 'Billing postal code (optional)'}
          </label>
          <input
            type="text"
            aria-label="Billing postal code"
            className={styles.fieldInput}
            value={postalCode}
            placeholder={postalCodeRequired ? 'Postal code' : 'Postal code (optional)'}
            onChange={(e) => setPostalCode(e.currentTarget.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Country</label>
          <select
            className={styles.fieldInput}
            aria-label="Billing country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {BILLING_COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        className={styles.primaryButton}
        disabled={submitting || !formValid}
        onClick={() => void handleSubmit()}
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </div>
  );
}
